import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography, alpha, Grid } from '@mui/material';
import { getClassificacaoColor } from './ClassificacaoBadge';
import ProgressBarWithMetas from './ProgressBarWithMetas';

interface EixoData {
  eixo: string;
  nome: string;
  score_medio: number;
  classificacao: string;
}

interface EixoBarChartProps {
  eixos: EixoData[];
  onEixoClick?: (eixo: string) => void;
  maxVisible?: number;
}

const EixoBarChart: React.FC<EixoBarChartProps> = ({
  eixos,
  onEixoClick,
  maxVisible,
}) => {
  const sorted = useMemo(
    () => [...eixos].sort((a, b) => b.score_medio - a.score_medio),
    [eixos],
  );

  const visible = maxVisible && sorted.length > maxVisible
    ? sorted.slice(0, maxVisible)
    : sorted;

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
          Desempenho por Eixo
        </Typography>
        <Grid container spacing={1}>
          {visible.map((entry) => {
            const color = getClassificacaoColor(entry.classificacao);
            return (
              <Grid item xs={12} sm={6} key={entry.eixo}>
                <Box
                  onClick={() => onEixoClick?.(entry.eixo)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1.5,
                    cursor: onEixoClick ? 'pointer' : 'default',
                    transition: 'background-color 0.15s',
                    '&:hover': onEixoClick ? { bgcolor: alpha(color, 0.06) } : {},
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      width: 120,
                      flexShrink: 0,
                      fontWeight: 500,
                      color: 'text.secondary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={entry.nome}
                  >
                    {entry.nome}
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <ProgressBarWithMetas value={entry.score_medio} color={color} height={8} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 32,
                      textAlign: 'right',
                      fontWeight: 700,
                      color,
                      fontSize: '0.75rem',
                    }}
                  >
                    {entry.score_medio?.toFixed(1) ?? '0'}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default EixoBarChart;
